import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { produce } from "immer";
import { useNavigate } from "react-router";

import {
  generateContent,
  imageLabeling,
  parseZoltarGenerateAssistantJson,
  readZoltarGenerateJsonWithFallback,
  assertZoltarGenerateOk,
  isZoltarResponseNotReadyError,
  ZOLTAR_POLL_USER_INPUT,
  listSystemPrompts,
} from "@app/services/zoltar";
import {
  fetchPersonaImages,
  fetchFireflyImage,
  uploadFireflyImage,
  DEFAULT_FIREFLY_MODEL,
} from "@app/services/adobeFirefly";

import { getElements } from "@app/services/ccLibraries";

import { store } from "../store";
import { importAllPersonas } from "@app/utils";

import { storyKey, useStory, useCreateStory } from "./stories";

export const zoltarKey = {
  all: ["zoltar"],
  queries: () => [...zoltarKey.all, "query"],
  query: (key) => [
    ...zoltarKey.queries(),
    ...(Array.isArray(key) ? key : [key]),
  ],
  images: () => [...zoltarKey.all, "images"],
  image: (key) => [
    ...zoltarKey.images(),
    ...(Array.isArray(key) ? key : [key]),
  ],
};

// How many times a pending agent response is re-requested before giving up.
const ZOLTAR_MAX_POLL_ATTEMPTS = 30;
const ZOLTAR_POLL_INTERVAL_MS = 2000;

// Poll state (thread id + poll input) of a running mutation, keyed by the
// variables object react-query hands to every retry of that same mutation.
const zoltarPollState = new WeakMap();

export const parseStoryPromptResponse = (response, id) => {
  const story = parseZoltarGenerateAssistantJson(
    response,
    "Failed to generate story",
  );

  story.id = id ?? self.crypto.randomUUID();

  // extend story script with dividers
  const sections = [
    ...new Set(story.script.slice(2, -1).map(({ section }) => section)),
  ];

  console.log("parseStoryPromptResponse sections:", sections);

  sections.forEach((section) => {
    const sectionIndex = story.script.findIndex(
      (step) => step.section === section,
    );

    if (sectionIndex !== -1) {
      story.script.splice(sectionIndex, 0, {
        section,
        title: section,
        text: "",
        personas: [],
        solutions: [],
        visual: "",
        visualMessaging: "",
        comments: "Section divider",
      });
    }
  });

  story.personas = produce(story.personas, (draft) => {
    draft.forEach((persona, idx) => {
      persona.id = idx;
    });
  });

  story.script = produce(story.script, (draft) => {
    draft.forEach((step, idx) => {
      step.id = idx;

      if (step?.personas) {
        step.personas = step.personas.map((name) =>
          story.personas.findIndex((persona) => persona.name === name),
        );
      }
    });
  });

  console.log("parseStoryPromptResponse story:", story);

  return story;
};

export const useGenerateStory = () => {
  // const queryClient = useQueryClient();
  const navigate = useNavigate();

  const createStory = useCreateStory();

  const mutation = useMutation({
    mutationFn: async () => {
      const ims = store.getState().ims;
      const {
        data,
        storyPrompt: prompt,
        storyPromptTemplate: promptTemplate,
      } = store.getState().wizard;

      if (!ims || !data || !promptTemplate) {
        throw new Error("Missing required data to generate story");
      }

      const promptStoryResponse = await generateContent(
        {
          user: ims.profile.email,
          data,
          prompt_template: [
            {
              prompt_template: "{{sources}}",
              role: "system",
            },
            {
              prompt_template: `You are the digital marketing expert specializing in ${data.industry} industry.`,
              role: "system",
            },
            {
              prompt_template: promptTemplate,
              role: "user",
            },
            {
              prompt_template:
                "{{#if (isEqual response_format 'json_object')}}You are an assistant designed to output JSON.{{/if}}{{~#if metadata}}{{#unless metadata.formattingOptions.renderMarkdown}}The answer should be in plain text without markdown or formatting.{{/unless}}{{/if}}",
              role: "system",
            },
          ],
        },
        "Failed to generate story",
      );

      const story = parseStoryPromptResponse(promptStoryResponse);

      return {
        ...story,
        data,
        prompt,
        promptTemplate,
      };
    },
    onMutate: async (variables) => {},
    onSuccess: async (data, variables) => {
      // save the story in db
      await createStory.mutateAsync(data);

      store.getState().resetWizard();
      navigate(`/stories/${data.id}`);
    },
    onError: (error, variables) => {
      console.error("Error generating story:", error);
    },
  });

  return mutation;
};

export const useGeneratePersonas = (storyId) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      const ims = store.getState().ims;
      const story = queryClient.getQueryData(storyKey.detail(storyId));

      if (!ims || !story?.industry) {
        throw new Error("Missing required data to generate personas");
      }

      store.getState().addToast(`Generating personas details`, {
        variant: "info",
        timeout: -1,
      });

      const personas = await importAllPersonas();

      const personasPromptTemplate = `Here are 2 documents: a customer journey story script in JSON format from ${story.industry} industry and a personas list in JSON format.

In the story script there's array "personas". For each persona write a prompt that will be used to describe the persona to generate their image – "descriptionPrompt". Include details of a person's appearance, ethnicity, characteristics, and detailed description of clothing.

Next, write a prompt that will be used to generate a profile portrait for this persona – "avatarPrompt". Portrait should be positioned in the center of the frame with a face in a front view. Include "descriptionPrompt" in this prompt. At the end of the prompt add "Neutral background. Professional photography style."

Next, for each persona in the story, match them with one of the existing personas from the personas list using the "descriptionPrompt" that you've just generated.

Consider the following matching criteria:
1. Gender compatibility (if story says it's female, find female persona, if story says it's male, find male persona)
2. Age range similarity (if story says it's 30-40 years old, find persona in the 30-40 age range)
3. Role/profession alignment (if story says it's a customer, find a persona with "Customer" role)
4. Description characteristics that match the descriptionPrompt (if story says it's a customer, find a persona with "Customer" role)

For each match, use the "id" from the personas list as "libraryId" and the "avatarId" from the personas list as "avatarId" for the story persona. Take the "id" and "avatarId" exactly as they are in the personas list, do not make them up nor change them.

Take into consideration context of all the data in the provided script. Adjust the prompts to represent specified "industry" as well as to be aligned with the whole story script.

Don't use personas names in the prompts, but rather use their gender.`;

      const promptPersonasResponse = await generateContent(
        {
          user: ims.profile.email,
          data: {},
          prompt_template: [
            {
              prompt_template: JSON.stringify(story),
              role: "system",
            },
            {
              prompt_template: JSON.stringify(personas),
              role: "system",
            },
            {
              prompt_template:
                "You are a prompt engineering expert specializing in image prompting.",
              role: "system",
            },
            {
              prompt_template: personasPromptTemplate,
              role: "user",
            },
            {
              prompt_template:
                "You are an assistant designed to output JSON. Please make sure to output JSON only.",
              role: "system",
            },
          ],
        },
        "Failed to fetch personas prompt",
      );

      console.log("promptPersonasResponse:", promptPersonasResponse);

      // Parse the response
      const personasContext =
        readZoltarGenerateJsonWithFallback(
          promptPersonasResponse,
          '{"personas": []}',
          "Failed to fetch personas prompt",
        )?.personas || [];

      console.log({ personasContext });

      return { personasContext };
    },
    onSuccess: ({ personasContext }) => {
      // Update the story with the generated persona details immediately
      // This makes persona data visible in the UI right away
      // Note: The actual save to server happens later in useGenerateImages
      queryClient.setQueryData(storyKey.detail(storyId), (oldData) =>
        produce(oldData, (state) => {
          personasContext.forEach((persona, idx) => {
            if (state.personas[persona?.id ?? idx]) {
              state.personas[persona?.id ?? idx].avatarPrompt =
                persona.avatarPrompt;
              state.personas[persona?.id ?? idx].descriptionPrompt =
                persona.descriptionPrompt;
              state.personas[persona?.id ?? idx].libraryId = persona.libraryId;
              state.personas[persona?.id ?? idx].avatarId = persona.avatarId;
            }
          });
        }),
      );

      store.getState().addToast("Personas details generated successfully", {
        variant: "positive",
        timeout: 5000,
      });
    },
    onError: (error) => {
      console.error("Error generating personas:", error);
      store.getState().addToast("Failed to generate personas details", {
        variant: "negative",
        timeout: 10 * 1000,
        actionLabel: "Try again",
        shouldCloseOnAction: true,
        onAction: () => {
          mutation.mutate();
        },
      });
    },
  });

  return mutation;
};

export const useGeneratePersonasImages = (storyId) => {
  const queryClient = useQueryClient();

  const storyQuery = useStory(storyId);
  const story = storyQuery.data;

  const mutation = useMutation({
    mutationFn: async () => {
      const ims = store.getState().ims;

      return Promise.allSettled(
        story?.personas?.map((persona) => {
          fetchPersonaImages(storyId, persona, ims);
        }),
      );
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: storyKey.details(storyId) });
    },
    onSuccess: (data) => {
      data.forEach((personaImages, idx) => {
        queryClient.setQueryData(storyKey.details(storyId), (oldData) =>
          produce(oldData, (state) => {
            state.personas[idx].avatarUrl = personaImages[0]?.[0]?.url;
            state.personas[idx].avatarSeed = personaImages[0]?.[0]?.seed;
            state.personas[idx].avatarPath = personaImages[0]?.[0]?.path;
          }),
        );
      });
    },
    onError: (error) => {
      console.error("Error generating personas images:", error);

      store.getState().addToast(`Failed to generate personas images`, {
        variant: "negative",
        timeout: -1,
        actionLabel: "Try again",
        shouldCloseOnAction: true,
        onAction: () => {
          mutation.mutate();
        },
      });
    },
  });

  return mutation;
};

export const useGenerateVisuals = (storyId, stepId = null) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      const ims = store.getState().ims;
      const story = queryClient.getQueryData(storyKey.detail(storyId));

      if (!ims || !ims.profile) {
        throw new Error(
          "IMS is not initialized. Please ensure you are authenticated.",
        );
      }

      if (!story?.industry) {
        throw new Error("Missing required data to generate visuals");
      }

      const { prompt_template, totalSteps, personas, script } =
        await preparePromptTemplate(story, stepId);

      const imageReferenceId = story.imageReference
        ? await uploadImageReference(story.imageReference)
        : null;

      const parseResponsePromises = [];
      const currentGenerationRef = { current: 0 };

      for await (const visualsContext of generateVisualsRecursive(
        ims,
        prompt_template,
        totalSteps,
        { steps: [] },
        10,
      )) {
        console.log("Visuals context:", visualsContext);
        parseResponsePromises.push(
          parseFetchVisualsResponse(
            queryClient,
            storyId,
            visualsContext,
            personas,
            imageReferenceId,
            totalSteps,
            currentGenerationRef,
          ),
        );
      }

      await Promise.all(parseResponsePromises);

      return {
        success: true,
        totalSteps,
        currentGeneration: currentGenerationRef.current,
      };
    },
    onSuccess: ({ totalSteps, currentGeneration }) => {
      //const successCount = currentGeneration;

      // Full success - all images generated
      store.getState().addToast("Image generation completed successfully", {
        variant: "positive",
        timeout: 60 * 1000, // 1 minute
        actionLabel: "Create Presentation",
        onAction: () => {
          store.setState({ isExpressDialogOpen: true });
        },
      });
    },
    onError: (error) => {
      console.error("Error generating visuals:", error);

      if (!stepId) {
        store.getState().addToast("Failed to generate story images", {
          variant: "negative",
          timeout: -1,
          actionLabel: "Try again",
          shouldCloseOnAction: true,
          onAction: () => {
            mutation.mutate();
          },
        });
      }
    },
  });

  return mutation;
};

export const getZoltarResponse = async (
  {
    promptTemplate,
    systemPromptId,
    promptOverrideKey,
    useAgent = true,
    model,
    schema,
    skills,
    threadId,
    userInput,
  } = {},
  { signal } = {},
) => {
  const ims = store.getState().ims;

  if (!ims || (!promptTemplate && !systemPromptId)) {
    throw new Error("Missing required data to generate data");
  }

  const response = await generateContent(
    {
      useAgent,
      user: ims.profile.email,
      prompt_template: promptTemplate,
      systemPromptId,
      promptOverrideKey,
      question: "",
      model,
      schema,
      skills,
      thread_id: threadId,
      user_input: userInput,
    },
    "Failed to generate content",
    { signal },
  );

  if (useAgent) {
    if (!response?.ok) {
      throw new Error("Failed to generate content", { cause: response });
    }
    return response;
  }

  const choice = response?.chat?.choices?.[0];
  const finishReason = choice?.finishReason;
  if (finishReason !== "stop") {
    // TODO: handle LLM error response
    throw new Error("Failed to generate content", { cause: response });
  }

  return {
    ok: true,
    response: choice?.message?.content ?? "",
    messages: (response?.message_history_window ?? []).map((m) => ({
      id: [
        "langchain_core",
        "messages",
        m.role === "user" ? "HumanMessage" : "SystemMessage",
      ],
      kwargs: { content: m.content },
    })),
  };
};

/**
 * The backend answers with a "response not yet ready" error once the agent hits
 * its own 60s timeout while it keeps producing the result. React Query retries
 * the mutation for us, reusing the thread id so the retry only asks the agent
 * to return the data instead of sending the prompt again.
 */
export const useSystemPrompts = (options = {}) =>
  useQuery({
    queryKey: ["zoltar", "prompts"],
    queryFn: ({ signal }) => listSystemPrompts({ signal }),
    staleTime: 60_000,
    ...options,
  });

export const useZoltarMutation = (options = {}, parseDataFn) => {
  const { retry, retryDelay, onSettled, ...restOptions } = options;

  return useMutation({
    mutationFn: async (variables, context) => {
      const {
        promptTemplate,
        systemPromptId,
        promptOverrideKey,
        useAgent = true,
        model,
        schema,
        skills,
        threadId,
        userInput,
      } = variables;
      const { signal } = context;

      let pollState = zoltarPollState.get(variables);
      if (!pollState) {
        pollState = { threadId: threadId ?? self.crypto.randomUUID() };
        zoltarPollState.set(variables, pollState);
      }

      try {
        const response = await getZoltarResponse(
          {
            promptTemplate,
            systemPromptId,
            promptOverrideKey,
            useAgent,
            model,
            schema,
            skills,
            threadId: pollState.threadId,
            userInput: pollState.userInput ?? userInput,
          },
          { signal },
        );

        console.log("useZoltarMutation response:", response);

        if (typeof parseDataFn === "function") {
          return parseDataFn?.(response, context);
        }

        return response;
      } catch (error) {
        if (isZoltarResponseNotReadyError(error)) {
          // The agent is still working on the same thread, so the retry below
          // only asks for the result instead of repeating the prompt.
          pollState.userInput = ZOLTAR_POLL_USER_INPUT;
        }

        throw error;
      }
    },
    retry:
      retry ??
      ((failureCount, error) =>
        isZoltarResponseNotReadyError(error) &&
        failureCount < ZOLTAR_MAX_POLL_ATTEMPTS),
    retryDelay: retryDelay ?? ZOLTAR_POLL_INTERVAL_MS,
    onSettled: (data, error, variables, onMutateResult, context) => {
      zoltarPollState.delete(variables);

      return onSettled?.(data, error, variables, onMutateResult, context);
    },
    ...restOptions,
  });
};

export const labelImageJson = async (
  { promptTemplate, systemPromptId, imageUrl, userInput },
  { signal } = {},
) => {
  const response = await imageLabeling(
    {
      imageUrl,
      prompt_template: promptTemplate,
      systemPromptId,
      userInput,
    },
    { signal },
  );

  console.log("useLabelImage response data:", response);

  const raw = response?.data?.tags?.[0]
    ?.replace(/^```json/, "")
    .replace(/```$/, "")
    .trim();

  // The service answers `tags: [""]` when the model reply was empty.
  if (!raw) {
    throw new Error("Image labeling returned an empty response.", {
      cause: response,
    });
  }

  return JSON.parse(raw);
};

export const useImageLabeling = (options = {}, parseDataFn) => {
  return useMutation({
    // queryKey: zoltarKey.image(key),
    mutationFn: async (variables, { signal }) => {
      const data = await labelImageJson(variables, { signal });

      return typeof parseDataFn === "function"
        ? parseDataFn(data, variables, { signal })
        : data;
    },
    // enabled: !!variables.imageUrl && !!variables.promptTemplate,
    // staleTime: 24 * 60 * 60 * 1000, // 24 hours
    // gcTime: 24 * 60 * 60 * 1000, // 24 hours
    meta: {
      errorMessage: "Failed to describe the image. Please try again.",
    },
    ...options,
  });
};

const preparePromptTemplate = async (story, stepId) => {
  const selectedStep = story.script?.find((s) => s.id === stepId);

  if (stepId && !selectedStep) {
    throw new Error(`No step found with id: ${stepId}`);
  }

  // Only fetch for personas with libraryId and that are mentioned in the script
  const libraries = [];

  let personas = story.personas
    .filter((persona) => persona.libraryId)
    .filter((persona) =>
      story.script.some((step) => step.personas?.includes(persona.id)),
    );

  console.log(
    "fetchPersonasLibraries:",
    personas.map((persona) => persona.name),
  );

  // Use Promise.all to properly handle async operations
  await Promise.all(
    personas.map(async (persona) => {
      try {
        const images = await getElements({
          libraryId: persona.libraryId,
          tags: [],
        });

        libraries.push({
          name: persona.name,
          libraryId: persona.libraryId,
          images: images?.elements?.map((image) => ({
            id: image.thumbnail?.componentId,
            name: image.name,
            description: image.details?.description,
          })),
        });
      } catch (error) {
        console.warn(
          `Failed to fetch elements for persona ${persona.name}:`,
          error,
        );
        // Continue with other personas even if one fails
      }
    }),
  );

  const extendedScript = produce(story.script, (draft) => {
    draft.forEach((step, sdx) => {
      step?.personas?.forEach((persona, pdx) => {
        const { name, descriptionPrompt, libraryId, avatarId } =
          story.personas.find((p) => p.id === persona) || {};

        if (!name || !descriptionPrompt) {
          console.warn(`No persona details found for id: ${persona}`);
        } else {
          draft[sdx].personas[pdx] = {
            name,
            descriptionPrompt,
            libraryId,
            avatarId,
          };
        }
      });
    });
  });

  const introductionTemplate = `Here are 2 documents: a customer journey story script as a JSON formatted array and a personas library as a JSON formatted array.

  The story is set in the ${story.industry} industry for the company called ${story.customer}.

  Title:
  ${story.title}

  Overview:
  ${story.storyOverview}

  Business goals:
  ${story.businessGoals}${
    !!story.campaignFocus
      ? `

  Campaign focus: ${story.campaignFocus}`
      : ""
  }`;

  const visualsPromptTemplate = `${selectedStep ? `For the step with an *id* ${selectedStep.id} and the *title* "${selectedStep.title}` : `For each item`} in the script write a prompt that will be used to generate a image visualizing this step – "visualPrompt".
  Using context of a step, describe in details the location in which it takes place.
  Suggest a generic background photo that would fit the context of the step. In this case, use a generic description of the location or setting which will represent the ${story.industry} industry. Don't include any people, logos, icons, or text messages in the prompt. Don't mention any companies.
  At the end of the prompt add "Professional photography style."

  Next, write a prompt that will be used to generate a visual for the step including specified personas – "personasPrompt". If there are specific personas mentioned in the "personas" field, include them in the scenario and use their "descriptionPrompt" to describe their appearance. Make sure to include all the details from "descriptionPrompt" field. Don't use personas names, but rather use their gender.
  At the end of the prompt add "Professional photography style."

  Take into consideration context of all the data in the provided script to with a cohesive narrative. Adjust prompts to represent specified "industry" as well as to be aligned with the whole story.
  If any prompt mentions a device, like a phone or a computer, describe it as "with the screen turned off" to avoid generating anything on the screen.

  Make sure to generate prompts for each item in the script.`;

  const personasPromptTemplate = `${selectedStep ? `For the step with an *id* ${selectedStep.id} and the *title* "${selectedStep.title}` : `For each item`} in the script find the best matching image from the provided personas library.

  1. For each persona mentioned in the step's "personas" field:
     - Find the matching persona in personas library by comparing the persona's "libraryId" field
     - Each persona item consists of "name", "libraryId", "images" fields.
     - Look through that persona's "images" array to find the best matching image.
     - Do not assume that the first image is the best match, but rather find the best match based on the scoring criteria.
     - Do not alter the original image's "id" or "name" fields. Take them exactly as they are in the library.

  2. When selecting the best image, consider:
     - The step's context and title
     - The persona's description and role in the step
     - The generated visualPrompt and personasPrompt
     - The image's "description" and "tags" fields
     - How well the image fits the step's setting and action (the image shouldn't be a generic setting, but rather a specific setting that matches the step's context)

  3. Scoring criteria for image selection:
     - Relevance to step context (highest priority)
     - Match with persona's role and description (if the persona is a customer, the image should be a customer setting)
     - Appropriate setting/environment (the image should be a specific setting that matches the step's context)
     - Professional appearance (the image should be a professional appearance)
     - Industry-appropriate attire and setting (the image should be an industry-appropriate attire and setting)

  4. For the selected image, save:
     - "libraryId": The persona's library ID
     - "visualId": The selected image's "id" field
     - "visualName": The selected image's "name" field

  IMPORTANT: Verify that the selected "visualId" exists in the same image object within the "images" array of the persona with the selected "libraryId".
  If they do not match the same image object, start over and find the correct matching image. If no personas are mentioned in the step, or if no suitable images are found, set both "libraryId" and "visualId" to null.
  `;

  const prompt_template = [
    {
      prompt_template: JSON.stringify(extendedScript),
      role: "system",
    },
    {
      prompt_template: JSON.stringify(libraries),
      role: "system",
    },
    {
      prompt_template: introductionTemplate,
      role: "user",
    },
    {
      prompt_template:
        "You are a prompt engineering expert specializing in image prompting.",
      role: "system",
    },
    {
      prompt_template: visualsPromptTemplate,
      role: "user",
    },
    {
      prompt_template: personasPromptTemplate,
      role: "user",
    },
    {
      prompt_template: `The output should contain a JSON formatted array of steps, where each step is an object with the following fields:
              steps:
                *idx* – The 0-based index of the step in the story script
                *title* – The title of the step
                *visualPrompt*
                *personasPrompt*
                *libraryId* – The library ID of the selected image (or null if none selected)
                *visualId* – The ID of the selected image (or null if none selected),
                *visualName* – The name of the selected image (or null if none selected)`,
      role: "user",
    },
    {
      prompt_template:
        "You are an assistant designed to output JSON. Please make sure to output JSON only.",
      role: "system",
    },
  ];

  return {
    prompt_template,
    totalSteps: stepId ? 1 : extendedScript.length,
    personas: libraries,
    script: extendedScript,
  };
};

const parseFetchVisualsResponse = async (
  queryClient,
  storyId,
  visualsContext,
  personas,
  imageReferenceId,
  totalSteps,
  currentGenerationRef,
) => {
  try {
    // Check IMS is initialized before making API calls
    const ims = store.getState().ims;
    if (!ims || !ims.profile) {
      console.error("IMS is not initialized. Cannot generate visuals.");
      throw new Error("IMS is not initialized");
    }

    // Update story with visual prompts and library selections
    // This makes the data visible in the UI immediately as each batch is processed
    queryClient.setQueryData(storyKey.detail(storyId), (oldData) =>
      produce(oldData, (state) => {
        visualsContext.steps?.forEach((step, idx) => {
          if (!state.script[step.idx ?? idx]) return;

          const visualPrompt = step?.personas?.length
            ? (step?.personasPrompt ?? step.visualPrompt)
            : step.visualPrompt;

          if (step.visualId && step.libraryId) {
            // Validate that the persona exists and has the specified image
            const persona = personas.find(
              (p) => p.libraryId === step.libraryId,
            );
            const hasValidImage = persona?.images?.some(
              (img) => img.id === step.visualId,
            );

            if (!hasValidImage) {
              console.warn(
                `Persona with libraryId ${step.libraryId} not found in personas array for step ${step.idx}`,
              );
              // Fall back to Firefly generation
              state.script[step.idx].visuals = [
                {
                  prompt: visualPrompt,
                  type: "firefly",
                },
              ];
            } else {
              // Validation passed - add library visual
              state.script[step.idx].visuals = [
                {
                  id: step.visualId,
                  libraryId: step.libraryId,
                  visualName: step.visualName,
                  prompt: visualPrompt,
                  type: "cc-library",
                },
              ];
            }
          } else {
            state.script[step.idx].visuals = [
              {
                prompt: visualPrompt,
                type: "firefly",
              },
            ];
          }
        });
      }),
    );

    store.getState().addToast(`Generating story images`, {
      variant: "info",
      timeout: -1,
    });

    // Generate Firefly images for steps without library visuals
    const visualsPromises = createVisualsPromises(
      queryClient,
      storyId,
      visualsContext,
      personas,
      imageReferenceId,
      totalSteps,
      currentGenerationRef,
    );

    const results = await Promise.allSettled(visualsPromises);

    // Analyze results to track successes and failures
    const succeeded = results.filter(
      (r) => r.status === "fulfilled" && r.value?.success,
    ).length;
    const failed = results.filter(
      (r) =>
        r.status === "rejected" ||
        (r.status === "fulfilled" && !r.value?.success),
    );

    if (failed.length > 0) {
      console.warn(`${failed.length} image(s) failed to generate:`, failed);
      const failedSteps = failed
        .map((r) =>
          r.status === "fulfilled" ? r.value?.title : "Unknown step",
        )
        .filter(Boolean);

      if (failedSteps.length > 0) {
        store
          .getState()
          .addToast(
            `Warning: ${failedSteps.length} step(s) failed to generate images`,
            {
              variant: "negative",
              timeout: 10000,
            },
          );
      }
    }

    console.log(
      `Batch complete: ${succeeded} succeeded, ${failed.length} failed`,
    );
  } catch (error) {
    console.error("Error parsing visuals response:", error);
    throw error;
  }

  return null;
};

const uploadImageReference = async (imageReference) => {
  try {
    console.log("Uploading reference image for style consistency...");
    const imageReferenceResponse = await uploadFireflyImage(imageReference.url);
    console.log("Reference image uploaded successfully");

    return imageReferenceResponse?.id;
  } catch (error) {
    // Reference image upload is optional - continue without it if it fails
    console.warn(
      "Failed to upload reference image - continuing without style reference:",
      error,
    );
    store
      .getState()
      .addToast(
        "Note: Reference image upload failed, generating without style reference",
        {
          variant: "info",
          timeout: 5000,
        },
      );

    return null;
  }

  return null;
};

/**
 * Generator function that recursively generates visuals for a story in batches.
 */
async function* generateVisualsRecursive(
  ims,
  prompt_template,
  length,
  result = { steps: [] },
  batchSize = 10,
) {
  for (let page = 1; page <= Math.ceil(length / batchSize); page++) {
    console.log(
      `Generating visuals for page ${page} of ${Math.ceil(length / batchSize)}`,
    );

    const currentPrompt = [...prompt_template];

    if (length > batchSize) {
      currentPrompt.splice(
        -1,
        0,
        {
          prompt_template: JSON.stringify(result),
          role: "system",
        },
        {
          prompt_template: `You have already generated ${result.steps.length} steps. Continue generating until you reach ${Math.min(length, result.steps.length + batchSize)} steps.`,
          role: "user",
        },
      );
    }

    const response = await generateContent(
      {
        user: ims.profile.email,
        prompt_template: currentPrompt,
      },
      "Failed to fetch visuals prompt",
    );

    const content = readZoltarGenerateJsonWithFallback(
      response,
      '{"steps": []}',
      "Failed to fetch visuals prompt",
    );

    result.steps.push(...content.steps);

    console.log({ content, result });

    yield content;
  }

  return result;
}

/**
 * Constructs the request body for the Firefly image generation API.
 */
const getFireflyImageBody = ({
  model = DEFAULT_FIREFLY_MODEL,
  prompt = "",
  contentClass = "photo",
  detailLevel = "full",
  size = { width: 2688, height: 1536 },
  seeds = [],
  imageReferenceUrl = null,
  uploadId = null,
  presets = [],
  styleStrength = 50,
  visualIntensity = 8,
  upsamplerType = "default",
} = {}) => ({
  model,
  prompt,
  contentClass,
  detailLevel,
  size,
  ...(seeds.length > 0 ? { seeds } : {}),
  ...(!!imageReferenceUrl || !!uploadId || presets.length > 0
    ? {
        style: {
          imageReference: {
            source: {
              ...(imageReferenceUrl ? { url: imageReferenceUrl } : {}),
              ...(uploadId ? { uploadId } : {}),
            },
          },
          presets,
          strength: styleStrength,
        },
      }
    : {}),
  visualIntensity,
  upsamplerType,
});

const createVisualsPromises = (
  queryClient,
  storyId,
  visualsContext,
  personas,
  imageReferenceId,
  totalSteps,
  currentGenerationRef,
) => {
  // Check IMS is initialized before making API calls
  const ims = store.getState().ims;
  if (!ims || !ims.profile) {
    console.error("IMS is not initialized. Cannot generate visuals.");
    return [];
  }

  const visualsPromises = (visualsContext.steps || []).map(async (step) => {
    try {
      const currentStory = queryClient.getQueryData(storyKey.detail(storyId));
      if (!currentStory) {
        console.warn(`No story found with id: ${storyId}`);
        return {
          success: false,
          step: step.idx,
          error: "Story not found",
        };
      }

      const storyStep = currentStory.script[step.idx];
      if (!storyStep) {
        console.warn(
          `No story step found for index ${step.idx} in visuals context`,
        );
        return {
          success: false,
          step: step.idx,
          error: "Step not found",
        };
      }

      const visual = storyStep.visuals[storyStep.visuals.length - 1];

      console.log("visual for step", step.idx, "is", storyStep.visuals);

      // Only generate Firefly image if no library visual was selected
      if (visual.type === "firefly") {
        if (!visual.prompt || typeof step.idx !== "number") {
          return {
            success: false,
            step: step.idx,
            error: "No prompt",
          };
        }

        console.log("visual prompt:", step.idx, visual.prompt);

        const visualImages = await fetchFireflyImage(
          storyId,
          getFireflyImageBody({
            prompt: visual.prompt,
            uploadId: imageReferenceId,
          }),
        );

        if (!visualImages?.length) {
          console.warn(
            `No images found for step ${step.idx} with prompt: ${visual.prompt}`,
          );
          return {
            success: false,
            step: step.idx,
            error: "No images returned",
          };
        }

        currentGenerationRef.current += 1;

        store
          .getState()
          .addToast(
            `Generating story images: ${currentGenerationRef.current}/${totalSteps}`,
            {
              variant: "info",
              timeout: -1,
            },
          );

        console.log("visual images:", step.idx, visualImages);

        // Add generated image to cache immediately so it appears in UI
        queryClient.setQueryData(storyKey.detail(storyId), (oldData) =>
          produce(oldData, (state) => {
            state.script[step.idx].visuals[
              state.script[step.idx].visuals.length - 1
            ] = {
              ...state.script[step.idx].visuals[
                state.script[step.idx].visuals.length - 1
              ],
              url: visualImages?.[0]?.url,
              seed: visualImages?.[0]?.seed,
              path: visualImages?.[0]?.path,
            };
          }),
        );
      }

      return { success: true, step: step.idx };
    } catch (error) {
      console.error(
        `Failed to fetch image for step ${step.title} (index ${step.idx}):`,
        error,
      );
      // Don't throw - return error info so we can continue with other images
      return {
        success: false,
        step: step.idx,
        error: error.message,
        title: step.title,
      };
    }
  });

  return visualsPromises;
};
